import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn } from "lucide-react";

// Wrap any image to make it click-to-enlarge in a fullscreen lightbox.
export default function ZoomImage({ src, alt, className, caption, testId }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    if (open) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className="group relative block w-full cursor-zoom-in"
        aria-label={`Enlarge ${alt}`}
      >
        <img loading="lazy" src={src} alt={alt} className={className} />
        <span className="absolute top-4 right-4 w-9 h-9 border border-zinc-600 bg-black/40 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
          <ZoomIn size={16} />
        </span>
      </button>

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {open && (
              <motion.div
                data-testid="image-lightbox"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                onClick={() => setOpen(false)}
                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/92 backdrop-blur-md p-6"
              >
                <button
                  data-testid="image-lightbox-close"
                  onClick={() => setOpen(false)}
                  className="absolute top-5 right-5 z-10 inline-flex items-center gap-2 h-11 px-4 bg-white text-black font-mono text-xs tracking-[0.18em] uppercase hover:bg-[#00e5ff] transition-colors shadow-lg"
                  aria-label="Close"
                >
                  <X size={18} strokeWidth={2.5} /> Close
                </button>
                <motion.div
                  initial={{ scale: 0.94, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.94, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  onClick={(e) => e.stopPropagation()}
                  className="relative w-full max-w-[92vw]"
                >
                  <img loading="lazy" src={src} alt={alt} className="w-full max-h-[90vh] object-contain" />
                  {caption && (
                    <p className="font-mono text-xs text-zinc-500 tracking-[0.2em] uppercase mt-4 text-center">
                      {caption}
                    </p>
                  )}
                  <p className="font-mono text-[10px] text-zinc-600 tracking-[0.2em] uppercase mt-3 text-center">
                    Click the Close button, tap anywhere, or press Esc to exit
                  </p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
}
